import React from "react";
import { X, DollarSign, Clock, User } from "lucide-react";
import { formatPrice } from "../../utils/formatters";

export default function ListingOffersModal({
  open,
  listing,
  offers,
  onClose,
  onStatusChange,
  updatingOfferId,
}) {
  if (!open || !listing) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-[#15161a] border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold">Offers for "{listing.title}"</h2>
            <p className="text-xs text-white/60">
              Review, accept or decline offers on this listing.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {(!offers || offers.length === 0) && (
            <p className="text-sm text-white/60 text-center py-8">
              No offers yet for this listing.
            </p>
          )}

          {offers?.map((offer) => {
            const listPrice =
              typeof listing.price_cents === "number"
                ? formatPrice(listing.price_cents, listing.currency || "AUD")
                : null;

            const offerPrice =
              typeof offer.amount_cents === "number"
                ? formatPrice(offer.amount_cents, offer.currency || "AUD")
                : `${offer.amount} ${offer.currency || "AUD"}`;

            const status = offer.status || "pending";
            const statusClasses =
              status === "accepted"
                ? "bg-green-500/10 text-green-300 border-green-500/40"
                : status === "declined"
                ? "bg-red-500/10 text-red-300 border-red-500/40"
                : "bg-amber-500/10 text-amber-200 border-amber-500/40";

            return (
              <div
                key={offer.id}
                className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold flex items-center gap-1">
                        <User className="w-3 h-3 text-white/50" />
                        <span className="truncate">
                          {offer.visitor?.display_name || "Buyer"}
                        </span>
                      </p>
                      {offer.visitor?.email && (
                        <p className="text-[11px] text-white/50 truncate">
                          {offer.visitor.email}
                        </p>
                      )}
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] border ${statusClasses}`}
                    >
                      {status}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-white/70 mb-1">
                    <div className="inline-flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      <span className="font-semibold">{offerPrice}</span>
                      {listPrice && (
                        <span className="text-white/40 text-[11px]">
                          (listing {listPrice})
                        </span>
                      )}
                    </div>
                    <div className="inline-flex items-center gap-1 text-white/50">
                      <Clock className="w-3 h-3" />
                      <span>
                        {offer.created_at
                          ? new Date(offer.created_at).toLocaleString()
                          : ""}
                      </span>
                    </div>
                  </div>

                  {status === "pending" && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      <button
                        disabled={updatingOfferId === offer.id}
                        onClick={() => onStatusChange(offer, "accepted")}
                        className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-xs font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {updatingOfferId === offer.id && "Updating..."}
                        {updatingOfferId !== offer.id && "Accept offer"}
                      </button>
                      <button
                        disabled={updatingOfferId === offer.id}
                        onClick={() => onStatusChange(offer, "declined")}
                        className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-xs font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {updatingOfferId === offer.id && "Updating..."}
                        {updatingOfferId !== offer.id && "Decline offer"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-white/10 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
